import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

import { useParams } from 'react-router-dom';
import { ButtonsCollection, DefaultAutoComplete, FormsCollection } from '@/ui-kit';
import { useKnowledgeBaseContext } from '@/views/knowledgeBase/context/useKnowledgeBaseContext';
import './style.sass';
import 'react-quill/dist/quill.snow.css';

import { FontAttributor } from './quill-custom';

// https://quilljs.com/docs/modules/toolbar
const quillToolbar = [
  [{ 'header': [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike", "blockquote", "link",
    { 'list': 'ordered' },
    { 'list': 'bullet' },
    { 'list': 'check' }
  ],
  [ { 'color': [] },
    { "background": [] },
    { "font": FontAttributor.whitelist },
    { "size": [] }
  ],
  [{ 'indent': '-1'}, { 'indent': '+1' }],
  ["direction"],
  [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
  [ "image", "video"],
];

export const ArticleForm = ({ onAction, submitButtonText }) => {
  const { t } = useTranslation();

  const { subcategoryId } = useParams();

  const { refreshCategories, categoriesData } = useKnowledgeBaseContext();

  useEffect(() => {
    refreshCategories()
  }, []);

  const categories = useMemo(() => {
    if (categoriesData.length) {
      return categoriesData.map((category) =>
        category.subCategories.map((subcategory) => ({
          label: `${subcategory.description} (${category.description})`,
          value: subcategory.id,
          isFixed: subcategory.id === Number(subcategoryId),
        }))).flat();
    }
      return [];
  }, [categoriesData])

  const { control, handleSubmit, setValue } = useFormContext();

  const onChangeAutocomplete = useCallback((items) => {
    const result = items.map(item => item.value);
    setValue('categoryIds', result);
  }, [setValue]);

  const getAutocompleteValue = (value) =>
    value.map(item => categories.find(option =>
      item === option.value));

  return (
    <FormsCollection.FormWrapper
      handleSubmit={handleSubmit(onAction)}
      classNameForm="article-form"
    >
      <div className="article-form__row">
        <div className="article-form__col">
          <label className="article-form__label" htmlFor="article-form_title">{t('Title')}</label>
          <Controller
            name="title"
            control={control}
            render={({
              touched = true,
              field: {
               onChange,
                value,
                onBlur
              },
              fieldState: {
               error
              },
            }) => (
              <FormsCollection.Input
                name="title"
                value={value}
                error={error?.message.message}
                onChange={onChange}
                onBlur={onBlur}
                touched={touched}
                id="article-form_title"
                placeholder="Title"
              />
            )}
          />
        </div>
        <div className="article-form__col">
          <label className="article-form__label" htmlFor="article-form_categoryIds">{t('Categories')}</label>
          <Controller
            name="categoryIds"
            control={control}
            render={({
              field: { value, onBlur }, touched,
            }) => (
              <DefaultAutoComplete
                isMulti
                placeholder={t('Choose categories')}
                options={categories}
                onChange={onChangeAutocomplete}
                onBlur={onBlur}
                touched={touched}
                value={getAutocompleteValue(value)}
                name="categoryIds"
                id="article-form_categoryIds"
              />
            )}
          />
        </div>
      </div>
      <div className="article-form__row">
        <div className="article-form__editor-wrapper">
          <label className="article-form__label" htmlFor="article-form_content">{t('Content')}</label>
          <Controller
            name="content"
            control={control}
            render={({
              field: { value, onBlur },
            }) => (
              <ReactQuill
                theme='snow'
                id="article-form_content"
                value={value}
                onBlur={onBlur}
                placeholder='Click here to start typing'
                onChange={(content) => {
                    setValue('content', content);
                  }}
                modules={{
                  toolbar: quillToolbar,
                  imageResize: {}
                }}
              />
            )}
          />
        </div>
      </div>
      <div className="article-form__row article-form__row--buttons">
        <ButtonsCollection.ButtonBrill
          type="submit"
          className="article-form__submit-button button-brill--fill"
        >
          {submitButtonText}
        </ButtonsCollection.ButtonBrill>
      </div>
    </FormsCollection.FormWrapper>
  )
}
